/**
 * Wilson score interval — 95% confidence interval for binomial proportions.
 *
 * Per Portal_Frontend_v2.1.1_Spec.md §15 + glossary:
 *   Wilson interval with z = 1.96
 *   z² = 3.8416
 *   z²/2 = 1.9208
 *   z²/4 = 0.9604
 *
 * Center:     (x + z²/2)            / (n + z²)
 * Half-width: z * sqrt(x*(n-x)/n + z²/4) / (n + z²)
 *
 * Equivalent canonical SQL (per spec §15 Cycle 3 Wilson CI half-width fix):
 *   1.96 * SQRT(x * (n - x) / n + 0.9604) / (n + 3.8416)
 *
 * Verified to 4 significant figures across 5 test cases in main spec Cycle 3.
 */

const Z = 1.96;
const Z_SQUARED = 3.8416;
const Z_SQUARED_HALF = 1.9208;
const Z_SQUARED_QUARTER = 0.9604;

export interface WilsonInterval {
  /** Point estimate (numerator / denominator). */
  rate: number;
  /** Wilson lower bound. */
  ciLower: number;
  /** Wilson upper bound. */
  ciUpper: number;
  /** Wilson center (≠ point estimate when n is small). */
  ciCenter: number;
  /** Wilson half-width. */
  halfWidth: number;
}

/**
 * Compute the Wilson 95% confidence interval for a binomial proportion.
 *
 * @param x numerator (successes)
 * @param n denominator (trials)
 * @returns WilsonInterval with rate, ciLower, ciUpper, ciCenter, halfWidth
 *
 * Edge cases:
 *  - n = 0: returns rate=0, ciLower=0, ciUpper=0 (degenerate; caller should handle)
 *  - x > n: throws RangeError
 *  - x or n negative: throws RangeError
 */
export function wilsonInterval(x: number, n: number): WilsonInterval {
  if (x < 0 || n < 0) throw new RangeError(`wilsonInterval: x and n must be non-negative (x=${x}, n=${n})`);
  if (x > n) throw new RangeError(`wilsonInterval: x must be ≤ n (x=${x}, n=${n})`);
  if (n === 0) {
    return { rate: 0, ciLower: 0, ciUpper: 0, ciCenter: 0, halfWidth: 0 };
  }

  const rate = x / n;
  const denom = n + Z_SQUARED;
  const ciCenter = (x + Z_SQUARED_HALF) / denom;
  // Per spec §15 canonical SQL: 1.96 * SQRT(x*(n-x)/n + 0.9604) / (n + 3.8416)
  const halfWidth = (Z * Math.sqrt((x * (n - x)) / n + Z_SQUARED_QUARTER)) / denom;
  const ciLower = Math.max(0, ciCenter - halfWidth);
  const ciUpper = Math.min(1, ciCenter + halfWidth);

  return { rate, ciLower, ciUpper, ciCenter, halfWidth };
}

/** Format a Wilson interval as "rate% (95% CI: lower–upper%)". */
export function formatWilson(w: WilsonInterval, decimals = 1): string {
  return `${(w.rate * 100).toFixed(decimals)}% (95% CI: ${(w.ciLower * 100).toFixed(decimals)}–${(w.ciUpper * 100).toFixed(decimals)}%)`;
}
